/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 */

import React, { Component, FunctionComponent, PropsWithChildren, ReactElement, ReactNode } from "react";
import { Grid, Tab, Tabs } from "@material-ui/core";
import { BackendFactory, Organization, User } from "@sade/data-access";
import InlineBox from "../ui/inline-box";
import TabPanel from "../ui/tab-panel";
import ViewAccessMethods from "../../ViewAccessMethods";
import OrganizationSelector from "../ui/organization-selector";
import DeviceList from "./components/device-list";
import OrganizationManagement from "./components/organization-management";
import UserList from "./components/user-list";
import accessControlled from "../access-control/access-controlled";
import NotificationManagement from "./components/notification-management";
import OtaManagement from "./components/ota-management";
import { translations } from "../../generated/translationHelper";

interface Props {}

interface State {
  selectedTab: number;
  homeOrganization?: Organization;
  selectedOrganization?: Organization;
  currentUser?: User;
  errorMsg?: string;
}

type TabPageProps = { currentPage: number; index: number };

const TabPage: FunctionComponent<PropsWithChildren<TabPageProps>> = (
  props: PropsWithChildren<TabPageProps>
): ReactElement => {
  return (
    <TabPanel value={props.currentPage} index={props.index}>
      <InlineBox>{props.children}</InlineBox>
    </TabPanel>
  );
};

export class AdminView extends Component<Props, State> {
  private readonly organizationBackend = BackendFactory.getOrganizationBackend();

  public constructor(props: Props) {
    super(props);
    this.state = {
      selectedTab: 0,
    };
  }

  public componentDidMount(): void {
    // let's not block in the componentDidMount
    this.initializeView().then();
  }

  private async initializeView(): Promise<void> {
    try {
      const [currentUser, homeOrganization] = await Promise.all([
        this.organizationBackend.getCurrentUser(),
        this.organizationBackend.getCurrentHomeOrganization(),
      ]);

      this.setState({
        currentUser,
        homeOrganization,
        selectedOrganization: homeOrganization,
      });
    } catch (err) {
      console.error("Could not fetch current user or home organization", err);
    }
  }

  private handleOrganizationSelected = (organization: Organization): void => {
    this.setState({
      selectedOrganization: organization,
    });
  };

  private handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
    console.log(`Changing page from ${this.state.selectedTab} to ${value}`);
    this.setState({ selectedTab: value });
  };

  private renderOrganizationStructure(): ReactNode {
    if (this.state.selectedOrganization) {
      return <OrganizationManagement organization={this.state.selectedOrganization} />;
    }
  }

  private renderUsersList(): ReactNode {
    if (this.state.selectedOrganization && this.state.currentUser) {
      return <UserList organization={this.state.selectedOrganization} currentUser={this.state.currentUser} />;
    }
  }

  private renderDeviceList(): ReactNode {
    if (this.state.selectedOrganization) return <DeviceList organization={this.state.selectedOrganization} />;
  }

  private renderOrganizationSelector(): ReactNode {
    if (this.state.homeOrganization) {
      return (
        <OrganizationSelector
          rootOrganization={this.state.homeOrganization}
          organizationSelected={this.handleOrganizationSelected}
        />
      );
    }
  }

  private renderNotificationManager(): ReactNode {
    if (this.state.selectedOrganization) {
      return <NotificationManagement organization={this.state.selectedOrganization} />;
    }
  }

  private renderOtaManagement(): ReactNode {
    if (this.state.selectedOrganization) {
      return <OtaManagement organization={this.state.selectedOrganization} />;
    }
  }

  public render(): ReactNode {
    return (
      <div style={{ margin: "1rem" }}>
        <Grid container={true} spacing={1} justifyContent={"center"}>
          <Grid item={true} md={12} lg={10} xl={8}>
            {this.renderOrganizationSelector()}
            <Tabs value={this.state.selectedTab} onChange={this.handlePageChange}>
              <Tab label={translations.admin.texts.users()} data-testid="users-tab" />
              <Tab label={translations.admin.texts.devices()} data-testid="devices-tab" />
              <Tab label={translations.admin.texts.organisation()} data-testid="organization-tab" />
              <Tab label={translations.admin.texts.notifications()} />
              <Tab label={translations.admin.texts.ota()} />
            </Tabs>
            <TabPage currentPage={this.state.selectedTab} index={0}>
              {this.renderUsersList()}
            </TabPage>
            <TabPage currentPage={this.state.selectedTab} index={1}>
              {this.renderDeviceList()}
            </TabPage>
            <TabPage currentPage={this.state.selectedTab} index={2}>
              {this.renderOrganizationStructure()}
            </TabPage>
            <TabPage currentPage={this.state.selectedTab} index={3}>
              {this.renderNotificationManager()}
            </TabPage>
            <TabPage currentPage={this.state.selectedTab} index={4}>
              {this.renderOtaManagement()}
            </TabPage>
          </Grid>
        </Grid>
      </div>
    );
  }
}

export default accessControlled(AdminView, ViewAccessMethods.hasAdminAccess);
